import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import katex from 'katex';

interface GuideFormulas {
  efficiency: SafeHtml;
  speedup: SafeHtml;
  idealSpeedup: SafeHtml;
  execPct: SafeHtml;
  deltaT: SafeHtml;
  strong: SafeHtml;
  scalab: SafeHtml;
  weak: SafeHtml;
  relativeStrong: SafeHtml;
  relativeScalab: SafeHtml;
  relativeWeak: SafeHtml;
  median: SafeHtml;
}

@Component({
  selector: 'app-guide-page',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './guide-page.component.html',
  styleUrl: './guide-page.component.scss'
})
export class GuidePageComponent {
  readonly formulas: GuideFormulas;

  constructor(private sanitizer: DomSanitizer) {
    this.formulas = {
      efficiency: this.renderBlock(String.raw`E(w,p)=\frac{T_{seq}(w)}{p\cdot T_{par}(w,p)}`),
      speedup: this.renderBlock(String.raw`S(w,p)=\frac{T_{seq}(w)}{T_{par}(w,p)}`),
      idealSpeedup: this.renderInline(String.raw`S_{ideal}(p)=p`),
      execPct: this.renderBlock(String.raw`\%Exec(region,w,p)=\frac{T_{region}(w,p)}{T_{parent}(w,p)}\times100`),
      deltaT: this.renderBlock(String.raw`\Delta T(region,w,p)=Imbalance(region,w,p)\times100`),
      strong: this.renderBlock(String.raw`Strong(w,p)=E(w,p)-E(w,p_1)`),
      scalab: this.renderBlock(String.raw`Scalab(w,p)=E(w,p)-E(w_1,p)`),
      weak: this.renderBlock(String.raw`Weak(w,p)=E(w,p)-E(w-m,p-m),\quad m=\min(w,p)`),
      relativeStrong: this.renderBlock(String.raw`RelStrong(w,p)=E(w,p)-E(w,p-1)`),
      relativeScalab: this.renderBlock(String.raw`RelScalab(w,p)=E(w,p)-E(w-1,p)`),
      relativeWeak: this.renderBlock(String.raw`RelWeak(w,p)=E(w,p)-E(w-1,p-1)`),
      median: this.renderBlock(String.raw`Median(x_1,\ldots,x_n)=\begin{cases}x_{(k)}&n=2k-1\\\frac{x_{(k)}+x_{(k+1)}}{2}&n=2k\end{cases}`)
    };
  }

  private renderInline(tex: string): SafeHtml {
    const html = katex.renderToString(tex, {
      throwOnError: false,
      displayMode: false
    });
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  private renderBlock(tex: string): SafeHtml {
    const html = katex.renderToString(tex, {
      throwOnError: false,
      displayMode: true
    });
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }
}
